import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { EventTagService } from "../../services/eventTagService";
import {
	AddEventTagFormData,
	AddEventTagLocalizable,
	ApiResponse,
	cultures,
	defaultAddEventTagFormData,
	EditEventTagFormData,
	EventTagDetail,
	EventTagListItem,
	OperationStatus,
	UpdateEventTagRequest,
} from "./constants";
import { getErrorMessage } from "../../utils/errorUtils";

interface EventTagContextState {
	// Core Domain Data
	eventTagList: EventTagListItem[];
	eventTagDetail: EventTagDetail | null;
	addEventTagForm: AddEventTagFormData;
	editEventTagForm: EditEventTagFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setAddEventTagForm: React.Dispatch<React.SetStateAction<AddEventTagFormData>>;
	setEditEventTagForm: React.Dispatch<React.SetStateAction<EditEventTagFormData | null>>;
    clearEventTagFormData: () => void;
    clearEventTagList: () => void;

    // Actions
    getEventTagList: () => Promise<void>;
    getEventTagDetails: (id: string) => Promise<void>;
    addEventTag: () => Promise<void>;
    updateEventTag: () => Promise<void>;
    removeEventTag: (id: string) => Promise<void>;
}

interface EventTagProviderProps {
    children: React.ReactNode;
}

const EventTagContext = createContext<EventTagContextState | undefined>(undefined);

export const useEventTagContext = () => {
	const context = useContext(EventTagContext);
	if (!context) {
		throw new Error("useEventTagContext must be used within an EventTagContextProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik Bir Hata oluştu.";
};

const isEventTagDataValid = (data: { tagIcon: string; eventTagLocalizable: AddEventTagLocalizable[] }) => {
	if (!data.tagIcon?.trim()) {
		return false;
	}
	return data.eventTagLocalizable.every(localizable => !!localizable.culture && !!localizable.tagName?.trim());
};

const buildUpdateRequest = (form: EditEventTagFormData, original: EventTagDetail): UpdateEventTagRequest | null => {
	const request: UpdateEventTagRequest = { eventTagId: original.eventTagId };

	if (form.tagIcon !== original.tagIcon) {
		request.tagIcon = form.tagIcon;
	}

	const changed = form.eventTagLocalizable
		.filter(item => {
			const before = original.eventTagLocalizable.find(o => o.eventTagLocalizableId === item.eventTagLocalizableId);
			return before?.tagName !== item.tagName.trim();
		})
		.map(item => ({
			eventTagLocalizableId: item.eventTagLocalizableId,
			culture: item.culture,
			tagName: item.tagName.trim(),
		}));

	if (changed.length) {
		request.eventTagLocalizable = changed;
	}

	return request.tagIcon === undefined && !request.eventTagLocalizable ? null : request;
};

export const EventTagContextProvider: React.FC<EventTagProviderProps> = ({ children }) => {
	const [eventTagList, setEventTagList] = useState<EventTagListItem[]>([]);
	const [eventTagDetail, setEventTagDetail] = useState<EventTagDetail | null>(null);
	const [addEventTagForm, setAddEventTagForm] = useState<AddEventTagFormData>(defaultAddEventTagFormData);
	const [editEventTagForm, setEditEventTagForm] = useState<EditEventTagFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	const navigate = useNavigate();

	const handleError = (error: unknown, operation: string) => {
		console.error(error);
		const errMsg = getErrorMessage(error);
		setOperationStatus({ type: "error", operation, message: errMsg });
		message.error(errMsg);
	};

	const clearEventTagFormData = useCallback(() => {
		setAddEventTagForm(defaultAddEventTagFormData);
		setEditEventTagForm(null);
		setEventTagDetail(null);
	}, []);

	const clearEventTagList = useCallback(() => {
		setEventTagList([]);
	}, []);

	const getEventTagList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventTagService.getEventTagList();

			if (response.isSuccessful) {
				setEventTagList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getEventTagDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventTagService.getEventTagDetails(id);

			if (response.isSuccessful) {
				const result = response.result;
				const ordered = cultures
					.map(culture => result.eventTagLocalizable?.find(item => item.culture === culture))
					.filter((item): item is EventTagDetail["eventTagLocalizable"][number] => !!item);

				if (ordered.length !== cultures.length) {
					message.error("Etiketin dil bilgileri eksik.");
					return;
				}

				const detail: EventTagDetail = { ...result, eventTagLocalizable: ordered };
				setEventTagDetail(detail);
				setEditEventTagForm({
					...detail,
					eventTagLocalizable: detail.eventTagLocalizable.map(item => ({ ...item })),
				});
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const addEventTag = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isEventTagDataValid(addEventTagForm)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await EventTagService.addEventTag(addEventTagForm);

			if (response.isSuccessful) {
				message.success("Etiket başarıyla eklendi.");
				setAddEventTagForm(defaultAddEventTagFormData);
				navigate("/event-tags");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "add");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [addEventTagForm, navigate]);

	const updateEventTag = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!editEventTagForm || !eventTagDetail) {
			message.error("Etiket bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const request = buildUpdateRequest(editEventTagForm, eventTagDetail);
		if (!request) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await EventTagService.updateEventTag(request);

			if (response.isSuccessful) {
				message.success("Etiket başarıyla güncellendi.");
				clearEventTagFormData();
				navigate("/event-tags");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "update");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [editEventTagForm, eventTagDetail, navigate, clearEventTagFormData]);

	const removeEventTag = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await EventTagService.removeEventTag(id);

			if (response.isSuccessful) {
				message.success("Etiket başarıyla silindi.");
				await getEventTagList();
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "delete");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const contextValue = useMemo<EventTagContextState>(
		() => ({
			eventTagList,
			eventTagDetail,
			addEventTagForm,
			editEventTagForm,
			operationStatus,
			setAddEventTagForm,
			setEditEventTagForm,
			clearEventTagFormData,
			clearEventTagList,
			getEventTagList,
			getEventTagDetails,
			addEventTag,
			updateEventTag,
			removeEventTag,
		}),
		[
			eventTagList,
			eventTagDetail,
			addEventTagForm,
			editEventTagForm,
			operationStatus,
			clearEventTagFormData,
			clearEventTagList,
			getEventTagList,
			getEventTagDetails,
			addEventTag,
			updateEventTag,
			removeEventTag,
		]
	);

	return <EventTagContext.Provider value={contextValue}>{children}</EventTagContext.Provider>;
};
